import { createFileRoute } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import AuthGuard from '@/components/auth-guard';
import { trpc } from '@/utils/trpc';
import { authClient } from '@/lib/auth-client';

export const Route = createFileRoute('/csv-import')({
  component: CSVImportPage,
});

interface ProcessedTransaction {
  description: string;
  amount: string;
  type: 'debit' | 'credit';
  date: string;
  category: string | null;
  installments?: number;
  installmentNumber?: number;
  originalAmount?: string;
}

interface CSVProcessingResult {
  success: boolean;
  message: string;
  extractionResult?: {
    success: boolean;
    transactions: ProcessedTransaction[];
    confidence: number;
    errors: string[];
    metadata: {
      csvType: 'bank_statement' | 'credit_card_bill';
      totalRows: number;
      processedRows: number;
      detectedBank?: string;
      detectedCard?: string;
    };
  };
  validatedTransactions?: any[];
  duplicates?: any[];
  duplicatesFiltered?: number;
  transactionsCreated?: number;
}

function CSVImportPage() {
  return (
    <AuthGuard>
      <CSVImportContent />
    </AuthGuard>
  );
}

function CSVImportContent() {
  const { data: session } = authClient.useSession();
  const [csvContent, setCsvContent] = useState('');
  const [fileName, setFileName] = useState('');
  const [processingResult, setProcessingResult] = useState<CSVProcessingResult | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  // Validation mutation
  const validateMutation = useMutation(trpc.csv.validateCSV.mutationOptions());

  // Processing mutation
  const processMutation = useMutation(trpc.csv.processCSV.mutationOptions());

  // Create transactions mutation
  const createMutation = useMutation(trpc.csv.createTransactions.mutationOptions());

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      alert('Por favor, selecione apenas arquivos CSV');
      return;
    }

    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      setCsvContent(content);
    };
    reader.readAsText(file);
  };

  const handleValidate = async () => {
    if (!csvContent) return;

    try {
      const result = await validateMutation.mutateAsync({
        csvContent,
        fileName,
      });

      if (result.success) {
        alert('✅ Formato CSV válido! Você pode prosseguir com o processamento.');
      } else {
        alert(`❌ Erro no formato CSV: ${result.error}`);
      }
    } catch (error) {
      alert('Erro ao validar CSV');
    }
  };

  const handleProcess = async (createTransactions = false) => {
    if (!csvContent) return;

    try {
      const result = await processMutation.mutateAsync({
        csvContent,
        createTransactions,
      });

      setProcessingResult(result);
      setShowPreview(true);

      if (createTransactions && result.transactionsCreated && result.transactionsCreated > 0) {
        alert(`✅ ${result.transactionsCreated} transações criadas com sucesso!`);
      }
    } catch (error) {
      alert('Erro ao processar CSV');
    }
  };

  const handleCreateTransactions = async () => {
    if (!processingResult?.validatedTransactions) return;

    try {
      const result = await createMutation.mutateAsync({
        transactions: processingResult.validatedTransactions.map(t => ({
          ...t,
          date: t.date.toISOString ? t.date.toISOString() : t.date,
        })),
      });

      if (result.success) {
        alert(`✅ ${result.transactionsCreated} transações criadas com sucesso!`);
        setProcessingResult(prev => prev ? {
          ...prev,
          transactionsCreated: result.transactionsCreated,
        } : null);
      } else {
        alert(`❌ ${result.message}`);
      }
    } catch (error) {
      alert('Erro ao criar transações');
    }
  };


  return (
    <div className="container mx-auto p-6">
      <h1 className="text-2xl font-bold mb-6">Importar Transações via CSV</h1>

      {/* File Upload */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">1. Selecionar Arquivo CSV</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Arquivo CSV (Extrato Bancário ou Fatura de Cartão)
            </label>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
          </div>

          {fileName && (
            <div className="text-sm text-gray-600">
              📄 Arquivo selecionado: <strong>{fileName}</strong>
            </div>
          )}

          {csvContent && (
            <div className="space-x-4">
              <button
                onClick={handleValidate}
                disabled={validateMutation.isPending}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {validateMutation.isPending ? 'Validando...' : 'Validar Formato'}
              </button>

              <button
                onClick={() => handleProcess(false)}
                disabled={processMutation.isPending}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
              >
                {processMutation.isPending ? 'Processando...' : 'Processar e Visualizar'}
              </button>

              <button
                onClick={() => handleProcess(true)}
                disabled={processMutation.isPending}
                className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
              >
                {processMutation.isPending ? 'Processando...' : 'Processar e Criar Transações'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Processing Results */}
      {showPreview && processingResult && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">2. Resultado do Processamento</h2>

          {/* Summary */}
          <div className="bg-gray-50 rounded p-4 mb-4">
            <h3 className="font-medium mb-2">Resumo</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <div className="text-gray-600">Tipo de CSV</div>
                <div className="font-medium">
                  {processingResult.extractionResult?.metadata.csvType === 'bank_statement'
                    ? 'Extrato Bancário'
                    : 'Fatura de Cartão'}
                </div>
              </div>
              <div>
                <div className="text-gray-600">Linhas Processadas</div>
                <div className="font-medium">{processingResult.extractionResult?.metadata.totalRows || 0}</div>
              </div>
              <div>
                <div className="text-gray-600">Transações Extraídas</div>
                <div className="font-medium">{processingResult.extractionResult?.transactions.length || 0}</div>
              </div>
              <div>
                <div className="text-gray-600">Confiança</div>
                <div className="font-medium">
                  {processingResult.extractionResult?.confidence
                    ? `${(processingResult.extractionResult.confidence * 100).toFixed(0)}%`
                    : '0%'}
                </div>
              </div>
            </div>

            {processingResult.duplicatesFiltered && processingResult.duplicatesFiltered > 0 && (
              <div className="mt-2 text-sm text-orange-600">
                ⚠️ {processingResult.duplicatesFiltered} duplicatas filtradas automaticamente
              </div>
            )}

            {processingResult.transactionsCreated && processingResult.transactionsCreated > 0 && (
              <div className="mt-2 text-sm text-green-600">
                ✅ {processingResult.transactionsCreated} transações criadas com sucesso
              </div>
            )}
          </div>

          {/* Errors */}
          {processingResult.extractionResult?.errors && processingResult.extractionResult.errors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded p-3 mb-4">
              <h4 className="font-medium text-red-800 mb-2">Avisos:</h4>
              <ul className="text-sm text-red-700 space-y-1">
                {processingResult.extractionResult.errors.map((error, index) => (
                  <li key={index}>• {error}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Transactions Preview */}
          {processingResult.validatedTransactions && processingResult.validatedTransactions.length > 0 && (
            <div>
              <h3 className="font-medium mb-3">Transações Identificadas</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full border border-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Descrição</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Valor</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Parcelas</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {processingResult.validatedTransactions.slice(0, 20).map((transaction, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-sm">{transaction.description}</td>
                        <td className="px-3 py-2 text-sm font-medium">
                          R$ {transaction.amount}
                        </td>
                        <td className="px-3 py-2 text-sm">
                          <span className={`px-2 py-1 rounded text-xs ${
                            transaction.type === 'credit'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {transaction.type === 'credit' ? 'Crédito' : 'Débito'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-sm">
                          {new Date(transaction.date).toLocaleDateString('pt-BR')}
                        </td>
                        <td className="px-3 py-2 text-sm">
                          {transaction.totalInstallments && transaction.totalInstallments > 1
                            ? `${transaction.installmentNumber}/${transaction.totalInstallments}`
                            : 'À vista'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {processingResult.validatedTransactions.length > 20 && (
                <div className="text-sm text-gray-500 mt-2">
                  Mostrando primeiras 20 de {processingResult.validatedTransactions.length} transações
                </div>
              )}

              {/* Create Transactions Button */}
              {!processingResult.transactionsCreated && processingResult.validatedTransactions.length > 0 && (
                <div className="mt-4">
                  <button
                    onClick={handleCreateTransactions}
                    disabled={createMutation.isPending}
                    className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium"
                  >
                    {createMutation.isPending
                      ? 'Criando Transações...'
                      : `Criar ${processingResult.validatedTransactions.length} Transações`}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Instructions */}
      <div className="bg-blue-50 rounded-lg p-6">
        <h2 className="text-lg font-semibold mb-4">ℹ️ Como usar</h2>
        <div className="space-y-2 text-sm text-gray-700">
          <p><strong>1. Formatos suportados:</strong> Arquivos CSV de extratos bancários e faturas de cartão</p>
          <p><strong>2. Detecção automática:</strong> O sistema identifica automaticamente se é extrato ou fatura</p>
          <p><strong>3. Parcelas:</strong> Para faturas de cartão, o sistema detecta parcelas automaticamente</p>
          <p><strong>4. Duplicatas:</strong> Transações duplicadas são filtradas automaticamente</p>
          <p><strong>5. Validação:</strong> Use "Validar Formato" para verificar se o CSV está correto antes de processar</p>
        </div>
      </div>
    </div>
  );
}